import { useEffect, lazy, Suspense } from "react";

import { useInView } from "react-intersection-observer";
// styles
import "../../index.css";
import "../../styles/scroll.css";
import "../../styles/notifStyle.css";
import "../../styles/sun.css";
import "../../styles/sections.css";

// components
import Nav from "../layout/Nav";
import Digital from "../module/Digital";
import WhatWeDo from "../module/WhatWeDo";
import StrategicDirections from "../module/StrategicDirections";
import TrustedPartnerships from "../module/TrustedPartnerships";
import LynquProduct from "../module/LynquProduct";
import FlatRentProduct from "../module/FlatRentProduct";
import SocialProof from "../module/SocialProof";
import TrustedInTheProcess from "../module/TrustedInTheProcess";
import AppsCompreomisses from "../module/AppsCompreomisses";
import SectionTeam from "../module/SectionTeam";
// (OurTeam / "Connect" section removed — the redesigned footer covers contact.)
import Footer from "../layout/Footer";
import ServicePopup from "../module/ServicePopup";
import Modal from "react-modal";

// actions and redux
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedServiceOrder,
  homeSelector,
} from "../featurs/homeSlice";
// custom hooks
import { useHandleIntersection } from "../hooks/useHandleIntersection";
import { useToggleJoinPopup } from "../hooks/useToggleJoinPopup";
import { useToggleServicesPopup } from "../hooks/useToggleServicesPopup";
import { useOffscreenAnimationPause, useScrollSnap } from "../hooks/useSectionActivity";

Modal.setAppElement("#root");

/* Lazy: JoinUsPopup pulls in the whole Firebase SDK (Firestore + Storage) for
 * its application form. react-modal renders no children while closed, so the
 * chunk only loads when someone actually opens "Join the team". */
const JoinUsPopup = lazy(() => import("../module/JoinUsPopup"));

/* Every section reveals ONCE. `triggerOnce` matters: without it a section that
 * scrolls out of view resets its own entrance and replays it on the way back,
 * which is what the three product sections used to do. */
const IN_VIEW = { threshold: 0.1, triggerOnce: false };

export default function Home() {
  const {
    isJoinPopupVisible,
    isServicesPopupVisible,
    selectedServiceOrder,
    servicesPopupStyle,
    navbarColor,
    statusbarColor,
    scrollPosition,
  } = useSelector(homeSelector);

  const dispatch = useDispatch();

  /* Section-theme observers. These only answer "which band is the nav over",
   * so they must keep firing in both directions. */
  const [section1Ref, section1InView] = useInView(IN_VIEW);
  const [section2Ref, section2InView] = useInView(IN_VIEW);
  const [section3Ref, section3InView] = useInView(IN_VIEW);
  const [section4Ref, section4InView] = useInView(IN_VIEW);
  const [section5Ref, section5InView] = useInView(IN_VIEW);
  const [section6Ref, section6InView] = useInView(IN_VIEW);
  const [section7Ref, section7InView] = useInView(IN_VIEW);
  const [sectionProductRef, sectionProductInView] = useInView(IN_VIEW);
  const [sectionFrbRef, sectionFrbInView] = useInView(IN_VIEW);
  const [sectionTrustRef, sectionTrustInView] = useInView(IN_VIEW);
  const [sectionFooterRef, sectionFooterInView] = useInView(IN_VIEW);

  /* Entrance observers are one-shot: an entrance that replays every time you
   * scroll back past it is not an entrance. */
  const [productRevealRef, productRevealed] = useInView({ threshold: 0.15, triggerOnce: true });
  const [frbRevealRef, frbRevealed] = useInView({ threshold: 0.15, triggerOnce: true });
  const [proofRevealRef, proofRevealed] = useInView({ threshold: 0.15, triggerOnce: true });
  const [notchRevealRef, notchRevealed] = useInView({ threshold: 0.15, triggerOnce: true });

  const toggleJoinPopup = useToggleJoinPopup();
  const toggleServicesPopup = useToggleServicesPopup();
  useHandleIntersection({
    section1InView,
    section2InView,
    section3InView,
    section4InView,
    section5InView,
    section6InView,
    section7InView,
    sectionProductInView,
    sectionFrbInView,
    sectionTrustInView,
    sectionFooterInView,
  });
  useOffscreenAnimationPause();
  useScrollSnap();

  const onContactUsClicked = () => {
    window.open("https://lynqu.com/book/maamjadi", "_blank", "noopener");
  };

  useEffect(() => {
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) themeMeta.setAttribute("content", statusbarColor);
    // Tint the page background too, so the iOS safe-area insets / overscroll
    // track the active section's colour instead of the default white.
    document.documentElement.style.backgroundColor = statusbarColor;
    document.body.style.backgroundColor = statusbarColor;
  }, [statusbarColor]);

  /* Scroll-reveal: fade + rise [data-reveal] elements as they enter view.
   * One observer for the whole page; each element reveals once, then
   * unobserves and drops its will-change. */
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    if (!els.length) return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      els.forEach((el) => el.classList.add("is-revealed"));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    els.forEach((el) => observer.observe(el));

    // Safety net: anything already on screen at load gets revealed even if the
    // observer's first callback lands late.
    const safety = setTimeout(() => {
      els.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) el.classList.add("is-revealed");
      });
    }, 1200);

    return () => {
      observer.disconnect();
      clearTimeout(safety);
    };
  }, []);

  /* Scroll lock while a popup is open. */
  useEffect(() => {
    if (isJoinPopupVisible || isServicesPopupVisible) {
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollPosition}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.documentElement.style.scrollBehavior = "auto";
      return () => {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        requestAnimationFrame(() => {
          document.documentElement.scrollTop = scrollPosition;
          document.documentElement.style.scrollBehavior = "";
        });
      };
    }
    return undefined;
  }, [isJoinPopupVisible, isServicesPopupVisible, scrollPosition]);

  const popupOpen = isJoinPopupVisible || isServicesPopupVisible;

  return (
    <div className="w-full">
      {/* The nav stays MOUNTED while a popup is open — unmounting it tore down
          and rebuilt its intersection observers on every open and close. It is
          hidden from sight and from the accessibility tree instead. */}
      <div aria-hidden={popupOpen ? "true" : undefined} className={popupOpen ? "invisible" : ""}>
        <Nav onContactUsClicked={onContactUsClicked} theme={navbarColor} />
      </div>

      <div id="cond">
        <Digital sectionRef={section1Ref} />

        <WhatWeDo sectionRef={section2Ref} onServiceClicked={toggleServicesPopup} />

        <TrustedPartnerships sectionRef={section4Ref} />

        <LynquProduct
          sectionRef={sectionProductRef}
          revealRef={productRevealRef}
          inView={productRevealed}
        />

        <FlatRentProduct
          sectionRef={sectionFrbRef}
          revealRef={frbRevealRef}
          inView={frbRevealed}
        />

        <TrustedInTheProcess sectionRef={section5Ref} />

        <StrategicDirections sectionRef={section3Ref} />

        <AppsCompreomisses
          sectionRef={section6Ref}
          revealRef={notchRevealRef}
          inView={notchRevealed}
        />

        <SectionTeam sectionRef={section7Ref} toggleJoinPopup={toggleJoinPopup} />

        <SocialProof
          sectionRef={sectionTrustRef}
          revealRef={proofRevealRef}
          inView={proofRevealed}
        />

        <Footer sectionRef={sectionFooterRef} />
      </div>

      {/* Escape is handled INSIDE JoinUsPopup so it goes through the same
          discard guard as the close button and the backdrop — react-modal
          would otherwise throw away a part-written application. Focus trapping
          and focus restore stay with react-modal. */}
      <Modal
        isOpen={isJoinPopupVisible}
        onRequestClose={toggleJoinPopup}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
        shouldReturnFocusAfterClose={true}
        shouldFocusAfterRender={true}
        style={{
          overlay: { backgroundColor: "transparent", zIndex: 100 },
          content: { background: "transparent", border: "none" },
        }}>
        <Suspense fallback={null}>
          <JoinUsPopup
            isJoinPopupVisible={isJoinPopupVisible}
            toggleJoinPopup={toggleJoinPopup}
          />
        </Suspense>
      </Modal>

      <ServicePopup
        isServicesPopupVisible={isServicesPopupVisible}
        toggleServicesPopup={toggleServicesPopup}
        selectedServiceOrder={selectedServiceOrder}
        updateSelectedServiceOrder={(order) => dispatch(setSelectedServiceOrder(order))}
        popupStyle={servicesPopupStyle}
      />
    </div>
  );
}
